"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.triggerInteractionAlloyAnalytics = exports.triggerPageAlloyAnalytics = void 0;
const tslib_1 = require("tslib");
const alloy_analytics_1 = tslib_1.__importDefault(require("@telus/alloy-analytics"));
/**
 * Default values for WebPageDetails.
 * These values are automatically merged with provided webPageDetails.
 */
const defaultWebPageDetails = {
    adobeDataLayerBuilder: 'alloyAnalytics',
    dataLayerType: 'acdl',
    siteGenerator: 'alloy',
    webApp: 'web',
    customPageViews: 1
};
/**
 * Triggers an analytics event using the Alloy Analytics library.
 *
 * This function sends analytics events to Adobe Analytics via the Alloy Analytics library.
 * It automatically merges provided webPageDetails with default values and fires the event.
 * Additionally, it supports tracking web interactions and product list items.
 *
 * EXAMPLE USAGE:
 * ```typescript
 * // Basic usage with default values
 * triggerPageAlloyAnalytics({
 *   eventType: 'pageLoaded'
 * })
 *
 * // With custom page details
 * triggerPageAlloyAnalytics({
 *   eventType: 'addToCart',
 *   webPageDetails: {
 *     pageName: 'Product Page',
 *     pageType: 'product',
 *     pageURL: 'https://koodomobile.com/cart',
 *     userAccountID: '12345'
 *   }
 * })
 *
 * // With web interactions
 * triggerPageAlloyAnalytics({
 *   eventType: 'chatInitiated',
 *   webInteractions: {
 *     eventType: 'chatInitiated',
 *     details: {
 *       name: 'Chat Start',
 *       type: 'UI',
 *       customProperty: 'value'
 *     }
 *   }
 * })
 *
 * // With product list items
 * triggerPageAlloyAnalytics({
 *   eventType: 'productImpressions',
 *   productListItems: [
 *     { productName: 'Phone A', category: 'Electronics', offerImpressions: 10 },
 *     { productName: 'Phone B', category: 'Electronics', offerImpressions: 5 }
 *   ]
 * })
 * ```
 *
 * @param {Object} params - The parameters for the analytics event.
 * @param {EventType} params.eventType - The type of event to track (e.g., 'pageLoaded', 'addToCart', 'purchase').
 * @param {Partial<WebPageDetails>} [params.webPageDetails={}] - Optional web page details to include with the event.
 * @param {Object} [params.webInteractions] - Optional web interaction details.
 * @param {EventType} [params.webInteractions.eventType] - The type of the web interaction event.
 * @param {WebInteractions} [params.webInteractions.details] - The details of the web interaction.
 * @param {Array<ProductListItem>} [params.productListItems] - Optional product list items to include with the event.
 *
 * @example
 * // Track a page load event
 * triggerPageAlloyAnalytics({
 *   eventType: 'pageLoaded',
 *   webPageDetails: {
 *     pageName: 'Home Page',
 *     pageType: 'landing',
 *     pageURL: 'https://koodomobile.com',
 *     userAccountID: 'user123'
 *   }
 * })
 *
 * @example
 * // Track a purchase event
 * triggerPageAlloyAnalytics({
 *   eventType: 'purchase',
 *   webPageDetails: {
 *     pageName: 'Checkout Complete',
 *     pageType: 'checkout',
 *     pageURL: 'https://koodomobile.com/checkout/complete',
 *     userAccountID: 'user123',
 *     province: 'ON'
 *   }
 * })
 *
 * @example
 * // Track a form completion
 * triggerPageAlloyAnalytics({
 *   eventType: 'formComplete',
 *   webPageDetails: {
 *     pageName: 'Registration Form',
 *     pageType: 'form',
 *     pageURL: 'https://koodomobile.com/register',
 *     userAccountID: 'user123',
 *     customPageViews: 3
 *   }
 * })
 *
 * @example
 * // Track a form completion with link click web interaction and product list items
 * triggerPageAlloyAnalytics({
 *   eventType: 'formComplete',
 *   webPageDetails: {
 *     pageName: 'Registration Form',
 *     pageType: 'form',
 *     pageURL: 'https://koodomobile.com/register',
 *     userAccountID: 'user123',
 *     customPageViews: 3
 *   },
 *   webInteractions: {
 *    eventType: 'linkClicks',
 *    details: {
 *      name: 'submit_link_click',
 *      type: 'other',
 *      linkName: 'submit_link'
 *    }
 *  },
 *   productListItems: [
 *     { productName: 'Phone A', category: 'Electronics', offerImpressions: 10 },
 *     { productName: 'Phone B', category: 'Electronics', offerImpressions: 5 }
 *   ]
 * })
 */
const triggerPageAlloyAnalytics = ({ eventType, webPageDetails = {}, webInteractions, commerce, productListItems }) => {
    const mergedWebPageDetails = Object.assign(Object.assign({}, defaultWebPageDetails), webPageDetails);
    let analyticsEvent = alloy_analytics_1.default.event(eventType).webPageDetails(mergedWebPageDetails);
    if (webInteractions) {
        analyticsEvent = analyticsEvent.webInteraction(webInteractions.eventType, webInteractions.details);
    }
    if (commerce) {
        analyticsEvent = analyticsEvent.commerce(commerce.eventType, commerce.details);
    }
    if (productListItems && productListItems.length > 0) {
        analyticsEvent = analyticsEvent.productListItems(productListItems);
    }
    analyticsEvent.fire();
};
exports.triggerPageAlloyAnalytics = triggerPageAlloyAnalytics;
/**
 * Triggers an Alloy Analytics event with the specified event type, web interactions,
 * and optionally a list of product list items.
 *
 * @param {Object} params - The parameters for the analytics event.
 * @param {EventType} params.eventType - The type of the event to be triggered.
 * @param {WebInteractions} params.webInteractions - The web interactions data associated with the event.
 * @param {Array<ProductListItem>} [params.productListItems] - An optional array of product list items
 *        to include in the analytics event.
 *
 * @example
 * // Trigger an interaction event with web interactions only
 * triggerInteractionAlloyAnalytics({
 *   eventType: 'linkClicks',
 *   webInteractions: {
 *     name: 'Footer Link Click',
 *     type: 'UI',
 *     linkName: 'Privacy Policy'
 *   }
 * })
 *
 * @example
 * // Trigger an interaction event with web interactions and product list items
 * triggerInteractionAlloyAnalytics({
 *   eventType: 'productImpressions',
 *   webInteractions: {
 *     name: 'Product Carousel Interaction',
 *     type: 'UI',
 *     carouselName: 'Featured Products'
 *   },
 *   productListItems: [
 *     { productName: 'Phone X', category: 'Electronics', offerImpressions: 15 },
 *     { productName: 'Tablet Y', category: 'Electronics', offerImpressions: 8 }
 *   ]
 * })
 *
 * @returns {void}
 */
const triggerInteractionAlloyAnalytics = ({ eventType, webInteractions, productListItems }) => {
    let analyticsEvent = alloy_analytics_1.default.event(eventType).webInteraction(webInteractions);
    if (productListItems && productListItems.length > 0) {
        analyticsEvent = analyticsEvent.productListItems(productListItems);
    }
    analyticsEvent.fire();
};
exports.triggerInteractionAlloyAnalytics = triggerInteractionAlloyAnalytics;
//# sourceMappingURL=index.js.map